import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { createAppKey } from "@/services/api/service";
import LanguageSync from "@/components/LanguageSync";
import GlobalLoading from "@/components/GlobalLoading";
import ScrollRestoration from "@/components/ScrollRestoration";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ConsoleLayout from "@/pages/ConsoleLayout";
import ConsoleLoginPage from "@/pages/ConsoleLoginPage";
import DashboardPage from "@/pages/DashboardPage";
import UserManagementPage from "@/pages/UserManagementPage";
import SocietiesPage from "@/pages/SocietiesPage";
import SocietyMembersPage from "@/pages/SocietyMembersPage";
import SponsorsPage from "@/pages/SponsorsPage";
import SponsorCheckPage from "@/pages/SponsorCheckPage";
import NotFoundPage from "@/pages/NotFoundPage";
import PublicLayout from "@/pages/public/PublicLayout";
import LandingPage from "@/pages/public/LandingPage";
import AboutPage from "@/pages/public/AboutPage";
import FeaturesPage from "@/pages/public/FeaturesPage";
import ForBusinessPage from "@/pages/public/ForBusinessPage";
import UniversitiesPage from "@/pages/public/UniversitiesPage";
import ContactPage from "@/pages/public/ContactPage";
import TermsPage from "@/pages/public/TermsPage";
import BusinessTermsPage from "@/pages/public/BusinessTermsPage";
import PrivacyPage from "@/pages/public/PrivacyPage";
import StudentTermsPage from "@/pages/public/StudentTermsPage";
import SignupLayout from "@/pages/signup/SignupLayout";
import SignupPage from "@/pages/signup/SignupPage";
import EmailVerificationPage from "@/pages/signup/EmailVerificationPage";
import ProfileIconPage from "@/pages/signup/ProfileIconPage";
import StudentCardPage from "@/pages/signup/StudentCardPage";
import HKIDPage from "@/pages/signup/HKIDPage";
import BasicInformationPage from "@/pages/signup/BasicInformationPage";
import SignupSuccessPage from "@/pages/signup/SignupSuccessPage";
import BusinessSignupLayout from "@/pages/businessSignup/BusinessSignupLayout";
import BusinessSignupPage from "@/pages/businessSignup/BusinessSignupPage";
import BusinessVerifyPage from "@/pages/businessSignup/BusinessVerifyPage";
import BusinessPhoneVerifyPage from "@/pages/businessSignup/BusinessPhoneVerifyPage";
import BusinessProfileIconPage from "@/pages/businessSignup/BusinessProfileIconPage";
import BusinessInformationPage from "@/pages/businessSignup/BusinessInformationPage";
import BusinessPlanPage from "@/pages/businessSignup/BusinessPlanPage";
import BusinessSuccessPage from "@/pages/businessSignup/BusinessSuccessPage";
import StudentLayout from "@/pages/student/StudentLayout";
import StudentHomePage from "@/pages/student/StudentHomePage";
import StudentEventDetailsPage from "@/pages/student/StudentEventDetailsPage";
import StudentCreateEventPage from "@/pages/student/StudentCreateEventPage";
import StudentEditEventPage from "@/pages/student/StudentEditEventPage";
import StudentMyEventsPage from "@/pages/student/StudentMyEventsPage";
import StudentActivitiesPage from "@/pages/student/StudentActivitiesPage";
import StudentActivityDetailsPage from "@/pages/student/StudentActivityDetailsPage";
import StudentCreateActivityPage from "@/pages/student/StudentCreateActivityPage";
import StudentEditActivityPage from "@/pages/student/StudentEditActivityPage";
import StudentCalendarPage from "@/pages/student/StudentCalendarPage";
import StudentCouponsPage from "@/pages/student/StudentCouponsPage";
import StudentRedemptionsPage from "@/pages/student/StudentRedemptionsPage";
import StudentSponsorTasksPage from "@/pages/student/StudentSponsorTasksPage";
import StudentNotificationsPage from "@/pages/student/StudentNotificationsPage";
import StudentChatPage from "@/pages/student/StudentChatPage";
import StudentChatMessagePage from "@/pages/student/StudentChatMessagePage";
import StudentAccountPage from "@/pages/student/StudentAccountPage";
import BusinessLayout from "@/pages/business/BusinessLayout";
import BusinessDashboardPage from "@/pages/business/BusinessDashboardPage";
import BusinessCouponsPage from "@/pages/business/BusinessCouponsPage";
import BusinessCouponCreatePage from "@/pages/business/BusinessCouponCreatePage";
import BusinessSponsorTasksPage from "@/pages/business/BusinessSponsorTasksPage";
import BusinessActivitiesPage from "@/pages/business/BusinessActivitiesPage";
import BusinessActivityDetailsPage from "@/pages/business/BusinessActivityDetailsPage";
import BusinessCreateActivityPage from "@/pages/business/BusinessCreateActivityPage";
import BusinessEditActivityPage from "@/pages/business/BusinessEditActivityPage";
import BusinessMessagesPage from "@/pages/business/BusinessMessagesPage";
import BusinessChatMessagePage from "@/pages/business/BusinessChatMessagePage";
import BusinessAccountPage from "@/pages/business/BusinessAccountPage";

/**
 * App shell: router + app-key bootstrap + global overlays.
 *
 * Route map:
 *   /                            – public marketing site (PublicLayout)
 *   /for-business                – partner business pitch page
 *   /terms, /privacy             – public Terms of Service / Privacy Policy
 *   /signup, /signup/*           – student signup flow (SignupLayout)
 *   /signup/success              – signup confirmation
 *   /business/signup, /business/signup/* – business signup flow (BusinessSignupLayout)
 *   /business/signup/success     – business signup confirmation
 *   /student/*                   – student area (guarded by StudentLayout)
 *   /student/events/:eventId     – single event details
 *   /student/events/new          – organizer event wizard (create)
 *   /student/events/:id/edit     – organizer event wizard (edit)
 *   /student/my-events           – events uploaded by the student's societies
 *   /student/activities          – activity feed (all / joining / created)
 *   /student/activities/new      – activity wizard (create)
 *   /student/activities/:id      – single activity details
 *   /student/activities/:id/edit – activity wizard (edit)
 *   /student/redeemed            – everything the student has redeemed
 *   /student/sponsor-tasks       – sponsor task proof submission screen
 *   /student/chat               – chat list
 *   /student/chat/:chatRoomId   – message thread of a chat room
 *   /login                       – Student / Business login
 *   /portal/*                    – business area (guarded by BusinessLayout)
 *   /portal/coupons/new          – business coupon creation
 *   /portal/activities           – business activity management
 *   /portal/activities/new       – business activity creation
 *   /portal/activities/:id       – business activity details + participants
 *   /portal/activities/:id/edit  – business activity edit
 *   /portal/chat/:chatRoomId     – message thread of a business chat room
 *   /console/login               – dedicated admin console login
 *   /console/*                   – admin console (guarded by ConsoleLayout)
 *   *                            – 404
 */
const App: React.FC = () => {
  useEffect(() => {
    createAppKey();
  }, []);

  return (
    <BrowserRouter>
      <ScrollRestoration />
      <LanguageSync />
      <GlobalLoading />
      <Routes>
        <Route path="/" element={<PublicLayout />}>
          <Route index element={<LandingPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="features" element={<FeaturesPage />} />
          <Route path="for-business" element={<ForBusinessPage />} />
          <Route path="universities" element={<UniversitiesPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="business-terms" element={<BusinessTermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="student-terms" element={<StudentTermsPage />} />
        </Route>
        <Route path="/signup" element={<SignupLayout />}>
          <Route index element={<SignupPage />} />
          <Route path="verify" element={<EmailVerificationPage />} />
          <Route path="profile" element={<ProfileIconPage />} />
          <Route path="student-card" element={<StudentCardPage />} />
          <Route path="hkid" element={<HKIDPage />} />
          <Route path="basic" element={<BasicInformationPage />} />
        </Route>
        <Route path="/signup/success" element={<SignupSuccessPage />} />
        <Route path="/business/signup" element={<BusinessSignupLayout />}>
          <Route index element={<BusinessSignupPage />} />
          <Route path="verify" element={<BusinessVerifyPage />} />
          <Route path="phone" element={<BusinessPhoneVerifyPage />} />
          <Route path="profile" element={<BusinessProfileIconPage />} />
          <Route path="business-info" element={<BusinessInformationPage />} />
          <Route path="plan" element={<BusinessPlanPage />} />
        </Route>
        <Route path="/business/signup/success" element={<BusinessSuccessPage />} />
        <Route path="/student" element={<StudentLayout />}>
          <Route index element={<Navigate to="/student/home" replace />} />
          <Route path="home" element={<StudentHomePage />} />
          <Route path="events" element={<Navigate to="/student/home" replace />} />
          <Route path="events/new" element={<StudentCreateEventPage />} />
          <Route path="events/:eventId" element={<StudentEventDetailsPage />} />
          <Route
            path="events/:eventId/edit"
            element={<StudentEditEventPage />}
          />
          <Route path="my-events" element={<StudentMyEventsPage />} />
          <Route path="activities" element={<StudentActivitiesPage />} />
          <Route path="activities/new" element={<StudentCreateActivityPage />} />
          <Route
            path="activities/:activityId"
            element={<StudentActivityDetailsPage />}
          />
          <Route
            path="activities/:activityId/edit"
            element={<StudentEditActivityPage />}
          />
          <Route path="calendar" element={<StudentCalendarPage />} />
          <Route path="redeem" element={<StudentCouponsPage />} />
          <Route path="redeemed" element={<StudentRedemptionsPage />} />
          <Route path="sponsor-tasks" element={<StudentSponsorTasksPage />} />
          <Route path="coupons" element={<Navigate to="/student/redeem" replace />} />
          <Route path="notifications" element={<StudentNotificationsPage />} />
          <Route path="chat" element={<StudentChatPage />} />
          <Route path="chat/:chatRoomId" element={<StudentChatMessagePage />} />
          <Route path="account" element={<StudentAccountPage />} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route
          path="/business/forgot-password"
          element={<ForgotPasswordPage mode="business" />}
        />
        <Route path="/business/login" element={<LoginPage mode="business" />} />
        <Route path="/portal" element={<BusinessLayout />}>
          <Route index element={<Navigate to="/portal/dashboard" replace />} />
          <Route path="dashboard" element={<BusinessDashboardPage />} />
          <Route path="coupons" element={<BusinessCouponsPage />} />
          <Route path="coupons/new" element={<BusinessCouponCreatePage />} />
          <Route path="sponsors" element={<BusinessSponsorTasksPage />} />
          <Route path="activities" element={<BusinessActivitiesPage />} />
          <Route path="activities/new" element={<BusinessCreateActivityPage />} />
          <Route
            path="activities/:activityId"
            element={<BusinessActivityDetailsPage />}
          />
          <Route
            path="activities/:activityId/edit"
            element={<BusinessEditActivityPage />}
          />
          <Route path="messages" element={<BusinessMessagesPage />} />
          <Route path="chat/:chatRoomId" element={<BusinessChatMessagePage />} />
          <Route path="account" element={<BusinessAccountPage />} />
        </Route>
        <Route path="/console/login" element={<ConsoleLoginPage />} />
        <Route path="/console" element={<ConsoleLayout />}>
          <Route index element={<Navigate to="/console/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="user-management" element={<UserManagementPage />} />
          <Route path="societies" element={<SocietiesPage />} />
          <Route path="societies/:societyId/members" element={<SocietyMembersPage />} />
          <Route path="sponsors" element={<SponsorsPage />} />
          <Route path="sponsors/:activityId" element={<SponsorCheckPage />} />
          <Route path="sponsors/user/:userId" element={<SponsorCheckPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;